import React, { useEffect, useState, useMemo, useRef } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import useYupValidation, {
  useYupSchemas,
  useHandleValidate,
} from "../../../../../hooks/useYupValidation";
import Breadcrumbs from "../../../../../components/Generic/Breadcrump";
import UITabMaterial, { PanelProps } from "../../../../../components/Generic/UITabMaterial";
import TitlePage from "../../../../../components/TitlePage";
import { ProductProvider, useProductContext } from "../context/ProductContext";
import DeleteProductsModal, {
  DeleteProductConfirmationModalHandles,
} from "./components/DeleteProductsModal";
import ProductActionsButtons from "./components/ProductActionsButtons";
import { ProductDetailsSkeleton } from "./components/Skeletons/details";
import { ProductBadgeStatus } from "./components/StatusBadge";
import { ProductDetails } from "./Details";
import useActionProduct from "./hooks/useAction";
import useStyles from "./styles";
import { BaseFieldProps, ProductSectionsProps } from "./types";
import { ProductDetailsFields, ProductFields, ProductTabs } from "./util";

interface ProductMaintenanceProps { }

interface ProductMaintenanceLocationParams {
  viewOnly?: boolean;
  productId?: string;
}

const ProductMaintenanceScreen: React.FC<ProductMaintenanceProps> = () => {
  const { state } = useLocation<ProductMaintenanceLocationParams>();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [viewOnly, setViewOnly] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [disableSave, setDisableSave] = useState<boolean>(false);
  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(ProductTabs.DETAILS);
  const deleteModalRef = useRef<DeleteProductConfirmationModalHandles>(null);

  const requestUuid = useMemo(() => uuid(), []);

  const productId = state?.productId;
  const { product, setProduct, setHasUnsavedChanges, hasUnsavedChanges } = useProductContext();

  const { getProductById, useSaveProduct, useDeleteProduct } = useActionProduct();

  const { data: productData, isInitialLoading: loadingProduct } = getProductById(Number(productId));

  const {
    mutate: saveProduct,
    isLoading: loadingSaveProduct,
    isPaused: pausedSaveProduct,
  } = useSaveProduct(() => {
    setHasUnsavedChanges(false);
  });

  const { mutate: deleteProduct, isLoading: loadingDeleteProduct } = useDeleteProduct(
    product?.id || 0
  );

  const validateAndSaveProduct = () => {
    if (!handleValidate() || !product) {
      setDisableSave(false);

      return;
    }

    if (loadingSaveProduct || pausedSaveProduct || loadingDeleteProduct) return;

    saveProduct(
      {
        id: product?.id,
        product,
        uuid: requestUuid,
      },
      {
        onSettled: () => setDisableSave(false),
      }
    );
  };

  const onDeleteProduct = () => {
    if (loadingSaveProduct || pausedSaveProduct || !product?.id) return;

    deleteProduct(product?.id);
  };

  const onCancel = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace("/app/products");
    }
  };

  useEffect(() => {
    if (productData) {
      setProduct((prev) => ({
        ...prev,
        ...productData,
      }));
    }
  }, [productData]);

  const { fieldHasError, getErrorByField, someFieldHasError } = useYupValidation(errors);
  const { stringRequired, numberRequired } = useYupSchemas(translate);

  useEffect(() => {
    if (state && state.viewOnly) setViewOnly(state.viewOnly);
  }, [state]);

  const schema = yup.object().shape({
    [ProductDetailsFields.SKU]: stringRequired,
    [ProductDetailsFields.DESCRIPTION]: stringRequired,
    [ProductDetailsFields.STATUS]: stringRequired,
    [ProductDetailsFields.DETAILS]: stringRequired,
    [ProductDetailsFields.CURRENCY]: stringRequired,
    [ProductDetailsFields.UNIT_PRICE]: numberRequired,
    [ProductDetailsFields.CUSTOMER_ID]: numberRequired,
  });

  const handleValidate = () => {
    setDisableSave(true);
    const validated = useHandleValidate(product, schema, setErrors);

    return validated;
  };

  const isUpdatingAction = useMemo(
    () => disableSave || loadingProduct || loadingProduct,
    [disableSave, loadingProduct, loadingProduct]
  );

  const generateBaseFieldProps = (name: ProductFields) => {
    const fieldProps: BaseFieldProps = {
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": `input-${name}`,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      disabled: viewOnly,
      name,
      ...(viewOnly ? { disabled: true } : {}),
    };

    return fieldProps;
  };

  const baseSectionProps: ProductSectionsProps = {
    generateBaseFieldProps,
    getErrorByField,
    fieldHasError,
    viewOnly,
    errors,
  };

  const detailsPanel: PanelProps = {
    key: ProductTabs.DETAILS,
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: loadingProduct,
    skeleton: ProductDetailsSkeleton,
    hasError: someFieldHasError(Object.values(ProductDetailsFields)),
    render: () => <ProductDetails {...baseSectionProps} />,
  };

  const leftPanels = [detailsPanel];

  const getSubtitle = () => {
    if (viewOnly) {
      return translate("viewProduct");
    }
    if (productId) {
      return translate("editProduct");
    }

    return translate("createProduct");
  };

  const openDeleteModal = () => deleteModalRef?.current?.open();

  return (
    <div className={classes.container}>
      <TitlePage
        left={
          <Breadcrumbs
            items={[
              {
                title: translate("product"),
                link: {
                  url: "/app/products",
                },
              },
              {
                title: getSubtitle(),
                color: "#465869",
              },
            ]}
          />
        }
        right={
          <Grid
            container
            item
            xs={12}
            sm={6}
            md={6}
            lg={6}
            className={classes.titleDataContainer}
            spacing={1}
          >
            <Grid item>{product?.status && <ProductBadgeStatus value={product?.status} />}</Grid>
            <Grid item>
              <span className={classes.titleData}>
                {product?.id ? ` ${translate("products")} #${product?.id}` : ""}
              </span>
            </Grid>
          </Grid>
        }
      />
      <Prompt when={hasUnsavedChanges} message="" />

      <div className={classes.tabs}>
        <UITabMaterial
          leftPanels={leftPanels}
          showRightPanel={false}
          showActivityButton={false}
          leftDataCy="left-product-tabs"
          onChangeLeftPanel={(_event, _panel, key) => key && setLeftPanelSelected(key)}
          tabFooter={
            <ProductActionsButtons
              loading={isUpdatingAction || loadingDeleteProduct}
              leftPanelSelected={leftPanelSelected}
              onCancel={onCancel}
              onDelete={() => openDeleteModal()}
              validateAndSaveProduct={validateAndSaveProduct}
              viewOnly={viewOnly}
              productAlreadySubmitted={false}
              productId={product?.id}
            />
          }
        />
      </div>
      <DeleteProductsModal
        ref={deleteModalRef}
        loadingAction={loadingDeleteProduct}
        onSubmit={onDeleteProduct}
      />
    </div>
  );
};

const ProductMaintenance = () => {
  return (
    <ProductProvider>
      <ProductMaintenanceScreen />
    </ProductProvider>
  );
};

export default ProductMaintenance;
